import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Mail, MailX, MailWarning, RefreshCw, ChevronDown, ChevronUp, Loader2 } from "lucide-react";

const TRIGGER_LABELS = {
  ticket_created: "Ticket created",
  ticket_status_change: "Status change",
  ticket_reminder: "Reminder",
  ticket_sla_breach: "SLA breach",
  ticket_dept_resolve: "Dept resolve link",
  ticket_cancellation: "Cancellation",
  daily_dept_report: "Daily report",
  weekly_dept_report: "Weekly report",
  monthly_dept_report: "Monthly report",
  sla_summary_report: "SLA summary",
  org_level_report: "Org-level report",
};

const STATUS_STYLE = {
  sent: { tint: "bg-emerald-50 text-emerald-700 border-emerald-200", Icon: Mail, label: "Sent" },
  failed: { tint: "bg-rose-50 text-rose-700 border-rose-200", Icon: MailX, label: "Failed" },
  skipped_no_poc: { tint: "bg-amber-50 text-amber-700 border-amber-200", Icon: MailWarning, label: "Skipped · no POC" },
};

const fmt = (iso) => new Date(iso).toLocaleString("en-GB", { dateStyle: "medium", timeStyle: "short" });

export default function TicketEmailTimeline({ ticketId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/email-log?ticket_id=${ticketId}&limit=50`);
      setRows(data || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (ticketId) load(); }, [ticketId]);

  return (
    <Card className="border-slate-200" data-testid="ticket-email-timeline">
      <CardHeader className="pb-3 flex flex-row items-center justify-between">
        <CardTitle className="font-heading text-base flex items-center gap-2">
          <Mail className="w-4 h-4 text-indigo-600" />
          Email activity
          <Badge variant="outline" className="text-[10px] ml-1 bg-slate-50">{rows.length}</Badge>
        </CardTitle>
        <Button variant="ghost" size="sm" onClick={load} className="text-slate-500" data-testid="email-timeline-refresh">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`} />
        </Button>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading && rows.length === 0 ? (
          <div className="text-center py-6 text-slate-400">
            <Loader2 className="w-4 h-4 animate-spin mx-auto" />
          </div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-xs text-slate-400">
            No emails dispatched for this ticket yet.
          </div>
        ) : (
          rows.map((r) => {
            const style = STATUS_STYLE[r.status] || STATUS_STYLE.sent;
            const Icon = style.Icon;
            const isOpen = expanded[r.id];
            return (
              <div
                key={r.id}
                className="border border-slate-100 rounded-md px-3 py-2 hover:bg-slate-50/60"
                data-testid={`email-row-${r.id}`}
              >
                <div className="flex items-start gap-2.5 text-sm">
                  <Icon className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-500" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-medium text-slate-800 text-xs">
                        {TRIGGER_LABELS[r.trigger] || r.trigger}
                      </span>
                      <Badge variant="outline" className={`text-[10px] ${style.tint}`}>
                        {style.label}
                      </Badge>
                      {r.fallback_used && (
                        <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">
                          fallback: {r.fallback_used}
                        </Badge>
                      )}
                      <span className="ml-auto text-[10px] text-slate-400">{fmt(r.created_at)}</span>
                    </div>
                    <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                      <span className="text-slate-400">→</span>{" "}
                      {(r.to || []).length > 0 ? r.to.join(", ") : <span className="italic">no recipient</span>}
                      {(r.cc || []).length > 0 && (
                        <span className="text-slate-400 ml-2">cc: {r.cc.join(", ")}</span>
                      )}
                    </div>
                    {(r.subject || r.error || r.provider_message_id) && (
                      <>
                        <button
                          type="button"
                          onClick={() => setExpanded({ ...expanded, [r.id]: !isOpen })}
                          className="text-[10px] text-indigo-600 hover:text-indigo-700 inline-flex items-center gap-0.5 mt-1"
                          data-testid={`email-row-toggle-${r.id}`}
                        >
                          {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          {isOpen ? "Hide details" : "Details"}
                        </button>
                        {isOpen && (
                          <div className="mt-1.5 rounded bg-slate-50 border border-slate-100 px-2.5 py-2 text-[11px] space-y-1">
                            {r.subject && (
                              <div><span className="text-slate-400">Subject:</span> <span className="text-slate-700">{r.subject}</span></div>
                            )}
                            {r.provider_message_id && (
                              <div><span className="text-slate-400">Message ID:</span> <span className="font-mono text-slate-600">{r.provider_message_id}</span></div>
                            )}
                            {r.error && (
                              <div><span className="text-slate-400">Error:</span> <span className="text-rose-600">{r.error}</span></div>
                            )}
                            {r.sent_by_name && (
                              <div><span className="text-slate-400">Sent by:</span> <span className="text-slate-600">{r.sent_by_name}</span></div>
                            )}
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
